import { io, Socket } from "socket.io-client";
import { API } from "../API";
import { EventSource } from "./EventSource";
import { createRealTimeManager, RealTimeManager } from "./RealTimeManager";

export function createSocketIOManager(
  api: API,
  listenersMap: Map<string, (payload: any) => void> = new Map()
): RealTimeManager {
  return createRealTimeManager(SocketIOManager, api, listenersMap);
}

/**
 * Maintains a persistent connection to the Socket.io service.
 */
class SocketIOManager implements RealTimeManager {
  private socket: Socket;
  private closed = false;

  constructor(api: API, listenersMap: Map<string, (payload: any) => void>) {
    this.socket = io(api.apiConfig.socketIOServerUrl);

    this.socket.on("connect", () => {
      console.log("[socket.io] connected");
      if (this.closed) {
        return;
      }
      this.socket.emit("auth", { token: api.authInfo.accessJWT });
    });

    this.socket.on("auth:done", () => {
      console.log("[socket.io] authenticated");
      if (this.closed) {
        return;
      }
      this.onConnected.broadcast(undefined);
    });

    this.socket.on("auth:error", (data) => {
      console.log("[socket.io] authentication failed", data);
      console.log("[socket.io] refreshing token");

      if (data.error !== "TokenIsInvalidError") {
        console.log("[socket.io] got fatal error, not attempting to reconnect");
        return;
      }

      api.refreshToken().then(() => {
        console.log("[socket.io] token refreshed");
        this.disconnect();
        this.reconnect();
      });
    });

    this.socket.on("disconnect", () => {
      console.log("[socket.io] disconnected");
      if (this.closed) {
        return;
      }
      this.onDisconnected.broadcast(undefined);
    });

    listenersMap.forEach((listener, eventName) => {
      this.socket.on(eventName, (payload: any) => {
        console.log(`[socket.io] event: ${eventName}, payload: ${JSON.stringify(payload)}`);
        if (this.closed) {
          return;
        }
        listener(payload);
      });
    });
  }

  public addListener(eventName: string, listener: (payload: any) => void): () => void {
    this.socket.on(eventName, listener);

    return () => this.socket.off(eventName, listener);
  }

  public close() {
    this.disconnect();
    this.closed = true;
  }

  public disconnect() {
    this.socket.disconnect();
  }
  public reconnect() {
    if (this.closed) {
      return;
    }
    this.socket.connect();
  }

  public readonly onConnected = new EventSource<void>();
  public readonly onDisconnected = new EventSource<void>();
}
